import {
  CalendarDays,
  CalendarRange,
  CircleDollarSign,
  Sparkles,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { SETUP_STEPS } from '@/lib/church-setup';

export interface WelcomeDialogProps {
  open: boolean;
  churchName: string;
  onStartSetup: () => void;
  onExplore: () => void;
  onBringRecords?: () => void;
}

const PREVIEW_STEP_IDS = ['funds', 'period', 'service', 'people'] as const;

const PREVIEW_ICONS: Record<(typeof PREVIEW_STEP_IDS)[number], LucideIcon> = {
  funds: CircleDollarSign,
  period: CalendarRange,
  service: CalendarDays,
  people: Users,
};

const previewSteps = SETUP_STEPS.filter((step) =>
  (PREVIEW_STEP_IDS as readonly string[]).includes(step.id),
);

/**
 * One-shot welcome after creating a church. Start setup expands the checklist;
 * Explore leaves it collapsed. Both clear welcomePending.
 */
export const WelcomeDialog = ({
  open,
  churchName,
  onStartSetup,
  onExplore,
  onBringRecords,
}: WelcomeDialogProps) => {
  const { t } = useTranslation('onboarding');
  const { t: tCommon } = useTranslation();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onExplore();
      }}
    >
      <DialogContent size="md" closeLabel={tCommon('action.close')} className="gap-0 p-0">
        <div className="border-b border-border bg-brand/[0.06] px-6 pt-6 pb-5">
          <div className="flex items-start gap-4 pr-8">
            <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand">
              <Sparkles className="size-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <DialogTitle className="text-card-title leading-snug">
                {t('welcome.title', { churchName })}
              </DialogTitle>
              <DialogDescription className="mt-2 text-body-sm leading-relaxed">
                {t('welcome.body')}
              </DialogDescription>
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          <p className="text-overline font-semibold tracking-wide text-fg-tertiary uppercase">
            {t('welcome.nextSteps')}
          </p>
          <ul className="mt-3 space-y-2">
            {previewSteps.map((step) => {
              const Icon = PREVIEW_ICONS[step.id as (typeof PREVIEW_STEP_IDS)[number]];
              return (
                <li
                  key={step.id}
                  className="flex items-center gap-3 rounded-lg border border-border bg-surface-sunken/60 px-3 py-2.5"
                >
                  <span
                    className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand"
                    aria-hidden
                  >
                    <Icon className="size-4" />
                  </span>
                  <span className="min-w-0 text-body-sm font-medium text-fg">{t(step.titleKey)}</span>
                </li>
              );
            })}
          </ul>
        </div>

        <DialogFooter className="flex-col gap-3 border-t border-border bg-surface-sunken/40 px-6 py-4 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-1 sm:gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onExplore}>
              {t('welcome.explore')}
            </Button>
            {onBringRecords ? (
              <Button type="button" variant="ghost" size="sm" onClick={onBringRecords}>
                {t('welcome.bringRecords')}
              </Button>
            ) : null}
          </div>
          <Button type="button" className="w-full sm:w-auto" onClick={onStartSetup}>
            {t('welcome.startSetup')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
